

import React, { useEffect, useState } from "react";
import InstitutionLayout from "../components/InstitutionLayout";
import { useAuth } from "../App";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const TARGET_ETAB_ID = "19036569-8d87-48c0-a886-36def8770f49";

function formatDuration(minutesFloat) {
  if (minutesFloat == null || isNaN(minutesFloat)) return "—";
  const totalMinutes = Math.round(minutesFloat);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
}

const STATUS_LABELS = {
  completed: "Complété",
  assigned: "Attribué",
  in_progress: "En cours",
  pending: "En attente",
  cancelled: "Annulé",
};

const STATUS_CLASSES = {
  completed: "text-green-600",
  assigned: "text-[#4F8585]",
  in_progress: "text-blue-600",
  pending: "text-gray-600",
  cancelled: "text-red-600",
};

function getStatusLabel(status) {
  return STATUS_LABELS[status] || status || "—";
}

function getStatusClass(status) {
  return STATUS_CLASSES[status] || "text-gray-500";
}

export default function InstitutionPayrolls() {
  const { axiosInstance, user } = useAuth();

  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );

  const [skip, setSkip] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchPrestations();
  }, [skip, selectedMonth, selectedYear]);

  const fetchPrestations = async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      const res = await axiosInstance.get("/prestations/institution", {
        params,
      });
      let data = res.data || [];

      data = data.filter((p) => {
        if (!p?.dateStart) return false;
        const d = new Date(p.dateStart);
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const year = d.getFullYear().toString();

        const monthMatch = selectedMonth ? month === selectedMonth : true;
        const yearMatch = selectedYear ? year === selectedYear : true;

        return monthMatch && yearMatch;
      });

      setPrestations(data);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du chargement des prestations");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () =>
    prestations.reduce(
      (acc, p) =>
        acc +
        (p.status === "completed" ? p.gain_final ?? p.final_rate ?? 0 : 0),
      0
    );

  const exportPDF = () => {
    if (prestations.length === 0) {
      toast.error("Aucune donnée à exporter");
      return;
    }

    const doc = new jsPDF("portrait", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    const periodLabel = selectedMonth
      ? `${new Date(0, Number(selectedMonth) - 1)
          .toLocaleString("fr-FR", { month: "long" })
          .toUpperCase()} ${selectedYear}`
      : `TOUTE L'ANNÉE ${selectedYear}`;

    const logo = new Image();
    logo.src = "/logo.jpeg";

    logo.onload = () => {
      doc.addImage(logo, "JPEG", 40, 30, 90, 45);

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(16);
      doc.text("SANA-CARE DIAMOND", pageWidth - 40, 50, { align: "right" });

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.text("Avenue louise 200, 1050 Bruxelles", pageWidth - 40, 65, {
        align: "right",
      });
      doc.text("Sana-carediamond@outlook.com", pageWidth - 40, 80, {
        align: "right",
      });
      doc.text("1025.941.977", pageWidth - 40, 95, { align: "right" });
      doc.text("BE71 3632 5061 2169", pageWidth - 40, 110, { align: "right" });

      doc.line(40, 125, pageWidth - 40, 125);

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20);
      doc.text(`FACTURATION - ${periodLabel}`, pageWidth / 2, 165, {
        align: "center",
      });

      doc.setFontSize(12);
      doc.setFont("Helvetica", "bold");
      doc.text("INSTITUTION :", 40, 205);

      doc.setFont("Helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Nom : ${user?.name || "—"}`, 40, 225);
      doc.text(`Adresse : ${user?.address || "—"}`, 40, 240);
      doc.text(`Contact : ${user?.email || "—"}`, 40, 255);
      doc.text(`Téléphone : ${user?.phone || "—"}`, 40, 270);

      doc.line(40, 305, pageWidth - 40, 305);

      const columns = [
        "Début",
        "Fin",
        "Soignant",
        "Durée brute",
        "Pause déduite",
        "Durée facturée",
        "Montant (€)",
        "Statut",
      ];

      const rows = prestations.map((p) => {
        const dureePayee = p.dureeMinutes || 0;
        const actualDuration = p.actual_duration || 0;
        const isTargetEtab = p.etablissement_id === TARGET_ETAB_ID;
        const isCompleted = p.status === "completed";

        const dureeBrute = isTargetEtab ? dureePayee + 30 : dureePayee;

        const dureeFinal = isCompleted
          ? p.actual_duration_payee || actualDuration || dureePayee
          : null;

        let pauseDeduite = p.pause_non_payee_minutes || 0;
        if (isTargetEtab && p.pause_non_payee_appliquee) {
          pauseDeduite = 30;
        }

        return [
          new Date(p.dateStart).toLocaleDateString(),
          new Date(p.dateEnd).toLocaleDateString(),
          p.assigned_name || "—",
          formatDuration(dureeBrute),
          pauseDeduite > 0 || (isTargetEtab && isCompleted)
            ? `- ${formatDuration(pauseDeduite > 0 ? pauseDeduite : 30)}`
            : "—",
          isCompleted && dureeFinal != null ? formatDuration(dureeFinal) : "—",
          isCompleted
            ? Number(p.gain_final ?? p.final_rate ?? 0).toFixed(2)
            : "—",
          getStatusLabel(p.status),
        ];
      });

      autoTable(doc, {
        startY: 320,
        head: [columns],
        body: rows,
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: {
          fillColor: [212, 235, 235],
          textColor: [41, 74, 74],
          fontSize: 10,
          halign: "center",
        },
        margin: { left: 40, right: 40 },
      });

      const finalY = doc.lastAutoTable.finalY + 20;

      doc.setFont("Helvetica", "italic");
      doc.setFontSize(9);

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(14);
      doc.text(
        `TOTAL À PAYER : ${calculateTotal().toFixed(2)} €`,
        pageWidth - 40,
        finalY + 20,
        { align: "right" }
      );

      doc.save(`facturation_${periodLabel}.pdf`);
    };
  };

  const canGoPrev = skip > 0;
  const canGoNext = prestations.length === limit;

  if (loading) return <div>Chargement...</div>;

  return (
    <InstitutionLayout title="Facturation - Sanacare">
      <div className="mb-6 flex gap-3 justify-between">
        <select
          className="border p-2 rounded"
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value);
            setSkip(0);
          }}
        >
          <option value="">Tous les mois</option>
          {Array.from({ length: 12 }).map((_, i) => {
            const month = (i + 1).toString().padStart(2, "0");
            return (
              <option key={month} value={month}>
                {new Date(0, i).toLocaleString("fr-FR", { month: "long" })}
              </option>
            );
          })}
        </select>

        <select
          className="border p-2 rounded"
          value={selectedYear}
          onChange={(e) => {
            setSelectedYear(e.target.value);
            setSkip(0);
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => {
            const y = (2022 + i).toString();
            return (
              <option key={y} value={y}>
                {y}
              </option>
            );
          })}
        </select>

        <button
          onClick={exportPDF}
          className="bg-[#6FAFAF] text-white px-5 py-2 font-semibold rounded hover:bg-[#4F8585] transition"
        >
          Exporter PDF
        </button>
      </div>

      <table className="w-full border border-gray-300 text-sm">
        <thead className="bg-[#D4EBEB]">
          <tr>
            <th className="border p-2">Date début</th>
            <th className="border p-2">Date fin</th>
            <th className="border p-2">Soignant</th>
            <th className="border p-2">Durée brute</th>
            <th className="border p-2">Pause déduite</th>
            <th className="border p-2">Durée facturée</th>
            <th className="border p-2">Montant (€)</th>
            <th className="border p-2">Statut</th>
          </tr>
        </thead>

        <tbody>
          {prestations.map((p) => {
            const dureePayee = p.dureeMinutes || 0;
            const actualDuration = p.actual_duration || 0;
            const isTargetEtab = p.etablissement_id === TARGET_ETAB_ID;
            const isCompleted = p.status === "completed";

            const dureeBrute = isTargetEtab ? dureePayee + 30 : dureePayee;

            const dureeFinal = isCompleted
              ? p.actual_duration_payee || actualDuration || dureePayee
              : null;

            let pauseDeduite = p.pause_non_payee_minutes || 0;
            if (isTargetEtab && p.pause_non_payee_appliquee) {
              pauseDeduite = 30;
            }

            return (
              <tr key={p._id} className="hover:bg-[#F4FAFA] border">
                <td className="p-2">
                  {new Date(p.dateStart).toLocaleDateString()}
                </td>
                <td className="p-2">
                  {new Date(p.dateEnd).toLocaleDateString()}
                </td>
                <td className="p-2">{p.assigned_name || "—"}</td>
                <td className="p-2">{formatDuration(dureeBrute)}</td>
                <td className="p-2 text-[#4F8585] font-semibold">
                  {pauseDeduite > 0 || (isTargetEtab && isCompleted)
                    ? `- ${formatDuration(pauseDeduite > 0 ? pauseDeduite : 30)}`
                    : "—"}
                </td>
                <td className="p-2 font-semibold">
                  {isCompleted && dureeFinal != null
                    ? formatDuration(dureeBrute)
                    : "—"}
                </td>
                <td className="p-2 font-semibold">
                  {isCompleted
                    ? `${Number(p.gain_final ?? p.final_rate ?? 0).toFixed(2)} €`
                    : "—"}
                </td>
                <td className="p-2 text-center">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusClass(
                      p.status
                    )}`}
                  >
                    {getStatusLabel(p.status)}
                  </span>
                </td>
              </tr>
            );
          })}

          <tr className="bg-[#D4EBEB] font-bold">
            <td colSpan={7} className="text-right p-2">
              Total à payer :
            </td>
            <td className="p-2">{calculateTotal().toFixed(2)} €</td>
          </tr>
        </tbody>
      </table>
{/* 
      <div className="mt-4 p-3 bg-[#F4FAFA] border border-[#B8DCDC] rounded text-sm text-gray-700">
        <p>
          <b>Note :</b> pour l’établissement concerné, une pause de 30 minutes
          non rémunérée est déduite. Pour les prestations non encore complétées,
          la durée facturée et le montant restent vides.
        </p>
      </div> */}

      <div className="mt-6 flex justify-between">
        <button
          disabled={!canGoPrev}
          onClick={() => setSkip(skip - limit)}
          className="px-4 py-2 bg-[#6FAFAF] text-white rounded disabled:opacity-50 hover:bg-[#4F8585] transition"
        >
          Précédent
        </button>
        <button
          disabled={!canGoNext}
          onClick={() => setSkip(skip + limit)}
          className="px-4 py-2 bg-[#6FAFAF] text-white rounded disabled:opacity-50 hover:bg-[#4F8585] transition"
        >
          Suivant
        </button>
      </div>
    </InstitutionLayout>
  );
}